/** Resolve payroll time inputs without changing legacy rows. */
export type WorkedTimePayInput<T extends Record<string, unknown>> = T & {
  actual_start: unknown;
  actual_end: unknown;
  break_minutes: unknown;
};

export class InvalidWorkedTimePayableInputError extends Error {
  readonly code = 'WORKED_TIME_PAYABLE_MISSING';

  constructor() {
    super('Worked-time pay times are missing for this timesheet. Re-open the worked-time review.');
    this.name = 'InvalidWorkedTimePayableInputError';
  }
}

export function workedTimePayInput<T extends Record<string, unknown>>(
  row: T,
): WorkedTimePayInput<T> {
  if (row.worked_time_evaluation_id == null) {
    return row as WorkedTimePayInput<T>;
  }
  return {
    ...row,
    actual_start: row.payable_start,
    actual_end: row.payable_end,
    break_minutes: row.payable_break_minutes,
  } as WorkedTimePayInput<T>;
}

/** Substitute persisted payable times at the row boundary, retaining raw observations for evidence. */
export function applyWorkedTimePayableToRow<T extends object>(input: T): T {
  // Accepts interface-typed rows (no index signature); fields are read through a record view.
  const row = input as unknown as Record<string, unknown>;
  if (row.worked_time_evaluation_id == null) return input;
  const start = row.payable_start;
  const end = row.payable_end;
  const breakMinutes = row.payable_break_minutes;
  const startMs =
    typeof start === 'string' || start instanceof Date ? Date.parse(String(start)) : NaN;
  const endMs = typeof end === 'string' || end instanceof Date ? Date.parse(String(end)) : NaN;
  if (
    start == null ||
    end == null ||
    breakMinutes == null ||
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    endMs <= startMs
  )
    throw new InvalidWorkedTimePayableInputError();
  const snapshot = row.break_treatment_snapshot;
  const snapshotRecord =
    snapshot && typeof snapshot === 'object' ? (snapshot as Record<string, unknown>) : null;
  const payableBreak = Number(breakMinutes);
  // Only the RECORDED break decides staleness. The deductible break is 0 by design for paid breaks
  // (break-treatment.ts), so comparing it would discard every valid paid-break snapshot.
  const snapshotBreakDiffers = Boolean(
    snapshotRecord &&
    typeof snapshotRecord.actualBreakMinutes === 'number' &&
    snapshotRecord.actualBreakMinutes !== payableBreak,
  );
  return {
    ...row,
    observed_actual_start: row.observed_actual_start ?? row.actual_start,
    observed_actual_end: row.observed_actual_end ?? row.actual_end,
    observed_break_minutes: row.observed_break_minutes ?? row.break_minutes,
    actual_start: start,
    actual_end: end,
    break_minutes: breakMinutes,
    ...(snapshotBreakDiffers ? { break_treatment_snapshot: null } : {}),
  } as unknown as T;
}
